import { Component, OnInit } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { User } from '@microsoft/microsoft-graph-types';
import { GraphService } from '@services/graph.service';
import { NavigationService } from '@services/navigation.service';
import { LogoutReasonCode, RouterLink } from '@shared/_constants';
import { UserService } from '@services/user.service';
import { LicensingCountry } from '@models/licensingCountry.model';
import { LoggingService } from '@services/logging.service';
import { Router } from '@angular/router';
import { LookupService } from '@services/lookup.service';
import { WebAPIService } from '@services/webapi.service';
import { DeactivationStatus } from '@services/active-component.service';
import { DialogService } from '@services/dialog.service';

@Component({
  selector: 'app-user-login-logout',
  templateUrl: './user-login-logout.component.html',
  styleUrls: ['./user-login-logout.component.css'],
})
export class UserLoginLogoutComponent implements OnInit {
  public currentUser: User;
  public loggedIn = false;
  public countryList: LicensingCountry[] = [];
  public selectedCountryId: number;
  public isToolAccess = true;
  public isImpersonation = false;

  constructor(
    private authService: AuthService,
    private graphService: GraphService,
    private navigationService: NavigationService,
    private userService: UserService,
    private logger: LoggingService,
    private router: Router,
    private lookup: LookupService,
    private webAPIService: WebAPIService,
    private dialogService: DialogService
  ) {}

  async ngOnInit() {
    // make sure to wait until the service is configured before finding out if they are logged in
    this.authService.configured.subscribe(async (r) => {
      if (r === true) {
        this.logger.info(this.constructor.name, 'ngOnInit', `authService.configured.subscribe`);

        this.loggedIn = this.authService.hasValidAzureADAccessToken();
        await this.getCurrentUserDetails();
        //this.getCountryList();

        this.webAPIService.hasValidWebAPIAccessToken().then((r2) => {
          if (r2 === true && this.currentUser !== undefined) {
            this.userService.IsToolAccessAvailable(this.currentUser).then((access) => {
              this.isToolAccess = access;
              if (access === true) {
                this.getCountryList();
                this.setCountry();
              }
            });
          }
        });
      }
    });

    // for making sure that if a user isn't logged in we set the flag and get the details when received
    this.authService.tokenReceived.subscribe(async (r) => {
      this.logger.info(this.constructor.name, 'ngOnInit', `authService.tokenReceived.subscribe`);

      this.loggedIn = r;
      await this.getCurrentUserDetails();
      this.webAPIService.hasValidWebAPIAccessToken().then((r2) => {
        if (r2 === true && this.currentUser !== undefined) {
          // const isToolAccess = this.userService.IsToolAccessAvailable(this.currentUser).;
          this.userService.IsToolAccessAvailable(this.currentUser).then((access) => {
            this.isToolAccess = access;
            if (access === true) {
              this.getCountryList();
              this.setCountry();
            }
          });
        }
      });
    });
  }

  public async logIn() {
    this.logger.info(this.constructor.name, 'logIn', 'Start');
    await this.authService.logIn();
    this.currentUser = await this.graphService.getCurrentUser();
    this.loggedIn = !this.loggedIn;
  }

  public async logOut() {
    const safetyCheckResult: DeactivationStatus = await this.authService.logoutSafetyCheck();
    if (safetyCheckResult.confirmationModalDisplayedToTheUser && safetyCheckResult.proceed) {
      return this.authService.logOut(LogoutReasonCode.userLoggedOut);
    }

    if (!safetyCheckResult.confirmationModalDisplayedToTheUser) {
      if (await this.dialogService.logoutConfirmation()) {
        return this.authService.logOut(LogoutReasonCode.userLoggedOut);
      }
    }

    return false;
  }

  private async getCurrentUserDetails() {
    this.logger.info(this.constructor.name, 'getCurrentUserDetails', 'Start');
    if (this.loggedIn) {
      await this.graphService.getCurrentUser().then((u) => (this.currentUser = u));
    }
  }

  private async getCountryList() {
    this.logger.info(this.constructor.name, 'getCountryList', 'Start');
    this.isImpersonation = await this.lookup.isImpersonationEnabled();

    if (this.isImpersonation === true) {
      this.countryList = await this.userService.getAllUserCountryList();
    }
  }

  public async onCountryChanged() {
    const result = await this.userService.updateUserCountry(this.selectedCountryId);
    if (result) {
      const userCountryId = await this.userService.getCountryId(this.currentUser);
      if (userCountryId && userCountryId !== null) {
        this.navigationService.userCountry = this.userService.allCountryList.find((z) => z.countryId === userCountryId);
      }
      this.router.navigate([RouterLink.MyStudies]);
    }
  }

  public async setCountry() {
    this.selectedCountryId = await this.userService.getCountryId(this.currentUser);
  }
}
